// Reglas puras de la Biblioteca (UI-SPEC §Interaction 2), sin React: qué función manda, cómo
// se fija y se suelta, qué sector hay bajo un punto y cuánto se mueve cada registro. Los dos
// árboles (escritorio y móvil) las comparten; solo cambian la geometría y la separación.
import {
    CLAVES_BIBLIOTECA,
    sectorDeAngulo,
    TRAZADO_ESCRITORIO,
    TRAZADO_MOVIL,
    type ClaveBiblioteca,
    type GeometriaBiblioteca,
    type RegistroBiblioteca,
} from '@/lib/marketing/geometriaBiblioteca';

export interface EntradasActiva {
    readonly sobre: number;
    readonly foco: number;
    readonly fijo: number;
    readonly destacado: number;
}

export interface Punto {
    readonly x: number;
    readonly y: number;
}

export interface Separacion {
    readonly base: number;
    readonly porAnillo: number;
}

export interface Desplazamiento {
    readonly mas: number;
    readonly dx: number;
    readonly dy: number;
}

// Centro de cada escenario en px de diseño (Biblioteca.dc.html 1240×580, Biblioteca_Movil 326×340):
// el de los trazados del fondo, para que el puntero y el dibujo no puedan descuadrarse.
export const CENTRO_ESCRITORIO: Punto = TRAZADO_ESCRITORIO.centro;
export const CENTRO_MOVIL: Punto = TRAZADO_MOVIL.centro;

// Lo que se aparta cada estante del sector activo (renderVals de los dos tableros).
export const SEPARACION_ESCRITORIO: Separacion = { base: 2, porAnillo: 1.4 };
export const SEPARACION_MOVIL: Separacion = { base: 1.2, porAnillo: 0.9 };

// Repulsión del puntero en escritorio: radio de alcance y empuje máximo (mover() del tablero).
const RADIO_EMPUJE = 54;
const EMPUJE_MAXIMO = 11;

// Prioridad hover > foco > fijado > destacado; −1 si nada está activo.
export function resolverActiva({ sobre, foco, fijo, destacado }: EntradasActiva): number {
    return [sobre, foco, fijo, destacado].find((indice) => indice >= 0) ?? -1;
}

// Pulsar la función fijada la suelta; pulsar otra (o fuera, k = −1) la sustituye.
export function alternarFijo(fijo: number, k: number): number {
    return k === fijo ? -1 : k;
}

export function indiceDestacado(destacado: ClaveBiblioteca | 'ninguno' | undefined): number {
    return destacado && destacado !== 'ninguno' ? CLAVES_BIBLIOTECA.indexOf(destacado) : -1;
}

// La función bajo un punto relativo al centro: solo dentro de la corona sensible de la
// geometría compartida, para que el héroe y la Biblioteca no puedan divergir.
export function funcionEnPunto(geometria: GeometriaBiblioteca, dx: number, dy: number): number {
    const distancia = Math.hypot(dx, dy);
    const { min, max } = geometria.coronaSensible;
    if (distancia < min || distancia > max) return -1;
    return sectorDeAngulo(dx, dy);
}

// Pasa una posición de pantalla a px de diseño: el escenario puede estar escalado.
export function puntoEnDiseno(clienteX: number, clienteY: number, caja: DOMRect, anchoDiseno: number): Punto {
    const escala = caja.width / anchoDiseno || 1;
    return { x: (clienteX - caja.left) / escala, y: (clienteY - caja.top) / escala };
}

function empuje(registro: RegistroBiblioteca, mas: number, puntero: Punto): Punto {
    const ex = registro.cx + mas * registro.cos - puntero.x;
    const ey = registro.cy + mas * registro.sin - puntero.y;
    const distancia = Math.hypot(ex, ey);
    if (distancia >= RADIO_EMPUJE) return { x: 0, y: 0 };
    const caida = 1 - distancia / RADIO_EMPUJE;
    const factor = (caida * caida * EMPUJE_MAXIMO) / (distancia || 1);
    return { x: ex * factor, y: ey * factor };
}

interface EntradaDesplazamiento {
    readonly registro: RegistroBiblioteca;
    readonly activa: boolean;
    readonly separacion: Separacion;
    // Relativo al centro, o null sin puntero.
    readonly puntero: Punto | null;
    readonly reducido: boolean;
}

// Separación del estante activo más la repulsión del puntero; con movimiento reducido, nada.
export function desplazamientoRegistro({ registro, activa, separacion, puntero, reducido }: EntradaDesplazamiento): Desplazamiento {
    if (reducido) return { mas: 0, dx: 0, dy: 0 };
    const mas = activa ? separacion.base + separacion.porAnillo * registro.anillo : 0;
    const { x, y } = puntero ? empuje(registro, mas, puntero) : { x: 0, y: 0 };
    return { mas, dx: x + 0, dy: y + 0 };
}

function redondear(valor: number): number {
    return Math.round(valor * 100) / 100 + 0;
}

// La transformación del tablero; el servidor y el cliente producen la misma cadena.
export function transformacionRegistro(registro: RegistroBiblioteca, centro: Punto, mas: number): string {
    const radio = redondear(registro.radio + mas);
    return `translate(${centro.x}px,${centro.y}px) rotate(${redondear(registro.angulo)}deg) translate(${radio}px,${-registro.grosor / 2}px)`;
}

// La repulsión va en la propiedad `translate`, que se compone por fuera de `transform`: así la
// escribe el fotograma sin tocar la transformación que pinta React.
export function traslacionEmpuje({ dx, dy }: Desplazamiento): string {
    return dx === 0 && dy === 0 ? '' : `${redondear(dx)}px ${redondear(dy)}px`;
}
